import { useCallback, useState } from 'react';

import apiClient from '@api-client';
import { Params$AdminCompanyFileController_create } from '@api-client/generated/client';
import { Schemas } from '@api-client/generated/types';

interface UploadStatus {
  isLoading: boolean;
  error: Error | null;
  success: boolean;
  progress: number;
  response?: Schemas.CompanyFile;
}

const UPLOAD_MAX_PERCENT = 100;

/**
 * A hook for uploading a file using `FileController_create` method of apiClient.
 * @param companyId - The ID of the company associated with the file upload.
 * @returns The upload status, including response, and a function to initiate the upload.
 */
export const useFileUpload = (companyId: string) => {
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>({
    isLoading: false,
    error: null,
    success: false,
    progress: 0,
  });

  const uploadFile = useCallback(
    (file: FormData) => {
      setUploadStatus((prevStatus) => ({
        ...prevStatus,
        isLoading: true,
        error: null,
        success: false,
        progress: 0,
      }));

      const params: Params$AdminCompanyFileController_create = {
        parameter: {
          companyId: companyId,
        },
        requestBody: file as unknown as Schemas.AdminCreateCompanyFileDto,
      };

      const config = {
        onUploadProgress: (progressEvent: {
          loaded: number;
          total?: number;
        }) => {
          if (!progressEvent.total) {
            return;
          }

          const percentCompleted = Math.min(
            Math.round((progressEvent.loaded * UPLOAD_MAX_PERCENT) / progressEvent.total),
            UPLOAD_MAX_PERCENT
          );

          setUploadStatus((prevStatus) => ({
            ...prevStatus,
            progress: percentCompleted,
          }));
        },
      };
      apiClient
        .AdminCompanyFileController_create(params, config)
        .then((response) => {
          setUploadStatus((prevStatus) => ({
            ...prevStatus,
            isLoading: false,
            error: null,
            success: true,
            progress: 100,
            response,
          }));
        })
        .catch((error: Error) => {
          setUploadStatus((prevStatus) => ({
            ...prevStatus,
            isLoading: false,
            error,
            success: false,
            progress: 0,
          }));
        });
    },
    [companyId]
  );

  return { uploadStatus, uploadFile };
};
